import { apiClient } from '../http/query-client';

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, maxAgeDays: number) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + maxAgeDays * 24 * 60 * 60 * 1000).toUTCString();
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  // Scope to the root domain (e.g. ".example.com") so the cookie survives
  // subdomain hops during checkout, except on IP hosts/localhost where
  // domain scoping isn't valid and the browser would reject the cookie.
  const isIp = /^\d+\.\d+\.\d+\.\d+$/.test(hostname);
  const parts = hostname.split('.');
  const rootDomain = !isIp && hostname !== 'localhost' && parts.length >= 2
    ? '.' + parts.slice(-2).join('.')
    : '';

  let cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  if (rootDomain) {
    cookie += `; domain=${rootDomain}`;
  }
  if (isHttps) {
    cookie += '; Secure';
  }
  document.cookie = cookie;
}

export function getFbc(): string | null {
  if (typeof window === 'undefined') return null;
  
  // Step 1: Check existing cookie (set by Pixel or prior script)
  const cookieVal = getCookie('_fbc');
  if (cookieVal && cookieVal !== 'undefined' && cookieVal !== 'null' && cookieVal.startsWith('fb.')) {
    // Copy raw string verbatim to localStorage if missing/out of sync without re-deriving or altering timestamp
    if (localStorage.getItem('_fbc') !== cookieVal) {
      localStorage.setItem('_fbc', cookieVal);
    }
    return cookieVal;
  }

  // Step 2: Check localStorage fallback (e.g. cookie dropped by Safari ITP)
  const lsVal = localStorage.getItem('_fbc');
  if (lsVal && lsVal !== 'undefined' && lsVal !== 'null' && lsVal.startsWith('fb.')) {
    // Restore raw string verbatim to cookie
    setCookie('_fbc', lsVal, 90);
    return lsVal;
  }

  return null;
}

export function getFbp(): string | null {
  if (typeof window === 'undefined') return null;
  
  // Read-only inspection of Meta Pixel's auto-generated _fbp cookie / localStorage
  const cookieVal = getCookie('_fbp');
  if (cookieVal && cookieVal !== 'undefined' && cookieVal !== 'null' && cookieVal.startsWith('fb.')) {
    if (localStorage.getItem('_fbp') !== cookieVal) {
      localStorage.setItem('_fbp', cookieVal);
    }
    return cookieVal;
  }

  const lsVal = localStorage.getItem('_fbp');
  if (lsVal && lsVal !== 'undefined' && lsVal !== 'null' && lsVal.startsWith('fb.')) {
    setCookie('_fbp', lsVal, 90);
    return lsVal;
  }

  return null;
}

// Keeps the cookie and localStorage copies of a Facebook attribution value in
// sync by copying raw strings verbatim (never re-deriving or re-parsing).
function syncFbStorage(name: string, value: string | null) {
  if (!value || typeof window === 'undefined') return;
  if (localStorage.getItem(name) !== value) {
    localStorage.setItem(name, value);
  }
  if (getCookie(name) !== value) {
    setCookie(name, value, 90);
  }
}

export function initFbc() {
  if (typeof window === 'undefined') return;

  // Step 1: Check if fbclid URL query parameter is present on landing
  const urlParams = new URLSearchParams(window.location.search);
  const fbclid = urlParams.get('fbclid');
  if (fbclid) {
    // Mint standard Meta _fbc with Unix timestamp in SECONDS: fb.1.{creation_time_unix_seconds}.{fbclid}
    const unixSeconds = Math.floor(Date.now() / 1000);
    const fbc = `fb.1.${unixSeconds}.${fbclid}`;
    setCookie('_fbc', fbc, 90);
    localStorage.setItem('_fbc', fbc);
    return;
  }

  // Step 2: If no fbclid in URL, preserve/sync existing _fbc from Cookie or localStorage
  getFbc();
}

// Run initFbc immediately upon script load in browser context to capture fbclid zero-delay
if (typeof window !== 'undefined') {
  initFbc();
}

export function trackFacebookEvent(
  eventName: string,
  customData: Record<string, any> = {},
  userData: Record<string, any> = {}
) {
  const hasFbq = typeof window !== 'undefined' && (window as any).fbq;
  const eventId = eventName.toLowerCase() + '_' + Math.random().toString(36).substring(2, 15);

  const fbcVal = getFbc() || undefined;
  const fbpVal = getFbp() || undefined;

  const enrichedUserData = {
    fbc: fbcVal,
    fbp: fbpVal,
    ...userData,
  };
  if (fbcVal && !enrichedUserData.fbc) enrichedUserData.fbc = fbcVal;
  if (fbpVal && !enrichedUserData.fbp) enrichedUserData.fbp = fbpVal;

  // 1. Fire Browser Event (Meta Pixel)
  if (hasFbq) {
    (window as any).fbq('track', eventName, customData, { eventID: eventId, ...enrichedUserData });
  }

  // 2. Fire Server Event (Conversions API via our backend proxy)
  apiClient.post('facebook/event', {
    event_name: eventName,
    event_id: eventId,
    user_data: enrichedUserData,
    custom_data: customData,
    event_source_url: typeof window !== 'undefined' ? window.location.href : undefined,
  }).catch(() => {});
}


